import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router';
import type { SupportTicketAttachmentInfo, SupportTicketThread } from '@/core/models/workspace.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { workspaceService } from '@/core/services/workspace';
import { Button } from '@/kit/Button';
import { TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Tooltip } from '@/kit/Tooltip';
import './SupportTicket.scss';
import './SupportTicketDetails.scss';

/** The reply's file rules, the same as a new ticket's (AddSupportTicket) and 3.0's ticket view. */
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const EXTENSIONS = ['.doc', '.docx', '.pdf', '.jpg', '.png', '.jpeg', '.txt', '.xls', '.xlsx', '.csv'];
const MAX_FILES = 10;

const statusHint = 'This ticket is with Capium support. Its status is set by the help desk and comes back from them.';

/** 3.0 showed each message's time as "hh:mm tt" over "ddd dd MMM yyyy", in the viewer's local time. */
function when(createdAtUtc: string, fallback: string): string {
  const value = dayjs(createdAtUtc);
  return value.isValid() ? value.format('ddd DD MMM YYYY, hh:mm A') : fallback;
}

function fileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return 'picture_as_pdf';
  if (contentType.startsWith('image/')) return 'image';
  return 'insert_drive_file';
}

function Attachments({ items }: { items: SupportTicketAttachmentInfo[] }) {
  if (items.length === 0) return null;
  return (
    <div className="support-thread-attachments">
      {items.map((attachment) => (
        <button
          key={attachment.id}
          type="button"
          className="support-thread-attachment"
          title={`Download ${attachment.fileName}`}
          onClick={() => {
            workspaceService.downloadSupportTicketAttachment(attachment.id).catch((error) => notifyError(error));
          }}
        >
          <Icon name={fileIcon(attachment.contentType)} outlined />
          <span>{attachment.fileName}</span>
        </button>
      ))}
    </div>
  );
}

interface MessageProps {
  author: string;
  fromSupport: boolean;
  time: string;
  message: string;
  attachments: SupportTicketAttachmentInfo[];
}

/** One entry on the thread: the requester's (green in 3.0) or the support team's (blue in 3.0). */
function Message({ author, fromSupport, time, message, attachments }: MessageProps) {
  return (
    <li className={`support-thread-item ${fromSupport ? 'from-support' : 'from-requester'}`}>
      <span className="support-thread-badge" aria-hidden="true">
        <Icon name={fromSupport ? 'support_agent' : 'person'} />
      </span>
      <div className="support-thread-panel">
        <div className="support-thread-heading">
          <span className="fw-bold">{author}</span>
          {fromSupport && <span className="support-thread-tag">Capium Support</span>}
          <span className="support-thread-time">{time}</span>
        </div>
        <p className="support-thread-message">{message}</p>
        <Attachments items={attachments} />
      </div>
    </li>
  );
}

/**
 * APT239: one support ticket and its conversation, as Capium 3.0's ticket view (Tickets/Ticket.aspx) showed it - the
 * ticket's details, the first message, every reply from the user and the support team oldest first, and a reply
 * box (message required, plus files) underneath. The status is the help desk's, so it is shown, never changed.
 */
export function SupportTicketDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [thread, setThread] = useState<SupportTicketThread | null>(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [messageInvalid, setMessageInvalid] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const sending = useRef(false);

  const load = useCallback(() => {
    if (!id) return;
    workspaceService.getSupportTicket(id).then(
      (loaded) => setThread({ ...loaded, attachments: loaded.attachments ?? [], replies: loaded.replies ?? [] }),
      (error) => {
        notifyError(error);
        navigate('/home/ticket');
      },
    );
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const next = [...files];
    Array.from(input.files ?? []).forEach((file) => {
      const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';
      if (!EXTENSIONS.includes(extension)) {
        notifyError(`${file.name} is not a supported file type.`);
      } else if (file.size === 0) {
        notifyError(`${file.name} is empty.`);
      } else if (file.size > MAX_FILE_BYTES) {
        notifyError(`${file.name} is larger than 2 MB.`);
      } else if (next.length >= MAX_FILES) {
        notifyError(`Attach at most ${MAX_FILES} files.`);
      } else if (!next.some((f) => f.name === file.name && f.size === file.size)) {
        next.push(file);
      }
    });
    setFiles(next);
    input.value = '';
  };

  const submit = () => {
    if (!id || sending.current) return;
    if (!message.trim()) {
      setMessageInvalid(true);
      notifyError('Enter the message.');
      return;
    }
    sending.current = true;
    setIsSending(true);
    workspaceService
      .saveSupportTicketReply(id, message.trim(), files)
      .then(
        (saved) => {
          notifySuccess(saved.message);
          setMessage('');
          setFiles([]);
          setMessageInvalid(false);
          load();
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        sending.current = false;
        setIsSending(false);
      });
  };

  return (
    <div className="ticket-wrapper support-ticket-page support-ticket-details">
      <div className="support-thread-inner">
        <div className="support-thread-back" onClick={() => navigate('/home/ticket')}>
          <Icon name="arrow_back" />
          <span className="link">Back to Support Tickets</span>
        </div>

        {!thread && <h2 className="m-0">Support Ticket</h2>}

        {thread && (
          <div className="support-thread-card">
            <div className="support-thread-header">
              <h2 className="m-0">
                {thread.ticket} - {thread.subject}
              </h2>
              <p className="m-0 text-gray">
                Raised by {thread.name} ({thread.email})
              </p>
            </div>

            <dl className="support-thread-meta">
              <div>
                <dt>Status</dt>
                <dd className="d-flex align-items-center gap-5">
                  <span className={thread.statusId === 0 || thread.statusId === 1 ? 'success' : ''}>
                    {thread.status}
                  </span>
                  <Tooltip title={statusHint} placement="right">
                    <Icon name="lock_outline" outlined className="font-size-16 text-primary" />
                  </Tooltip>
                </dd>
              </div>
              <div>
                <dt>Priority</dt>
                <dd>{thread.priority}</dd>
              </div>
              <div>
                <dt>Department</dt>
                <dd>{thread.department || '-'}</dd>
              </div>
              <div>
                <dt>Module</dt>
                <dd>{thread.module || '-'}</dd>
              </div>
              <div>
                <dt>Company</dt>
                <dd>{thread.company || '-'}</dd>
              </div>
              <div>
                <dt>Created on</dt>
                <dd>{when(thread.createdAtUtc, thread.date)}</dd>
              </div>
            </dl>

            <ul className="support-thread" aria-label="Ticket conversation">
              <Message
                author={thread.name}
                fromSupport={false}
                time={when(thread.createdAtUtc, thread.date)}
                message={thread.message}
                attachments={thread.attachments}
              />
              {thread.replies.map((reply) => (
                <Message
                  key={reply.id}
                  author={reply.authorName}
                  fromSupport={reply.isFromSupport}
                  time={when(reply.createdAtUtc, reply.date)}
                  message={reply.message}
                  attachments={reply.attachments}
                />
              ))}
            </ul>
            {thread.replies.length === 0 && (
              <p className="support-thread-empty">No replies yet. Capium support will respond here.</p>
            )}

            <form className="support-thread-reply" onSubmit={(event) => event.preventDefault()}>
              <div className="form-field textarea mb-1">
                <label className="fw-bold d-block mb-5" htmlFor="support-ticket-reply">
                  Message <span className="text-red">*</span>
                </label>
                <TextareaField
                  id="support-ticket-reply"
                  rows={6}
                  fieldClassName="wp-100"
                  maxLength={4000}
                  invalid={messageInvalid}
                  value={message}
                  disabled={isSending}
                  onChange={(event) => {
                    setMessage(event.target.value);
                    if (event.target.value.trim()) setMessageInvalid(false);
                  }}
                />
              </div>
              <div className="form-field attached mb-1">
                <p className="fw-bold">Attachments</p>
                <div className="attachFile">
                  <Icon name="cloud_upload" outlined />
                  <input
                    multiple
                    type="file"
                    autoComplete="off"
                    id="support-ticket-reply-file"
                    aria-label="Attach files to the reply"
                    accept={EXTENSIONS.join(',')}
                    disabled={isSending}
                    onChange={onFilesSelected}
                    className="cursor-pointer"
                  />
                  <label htmlFor="support-ticket-reply-file" className="cursor-pointer">
                    <strong>Choose a file</strong> or drag it here to attach it to your reply.
                  </label>
                </div>
                <span className="font-size-12">
                  (Max file size - 2 MB) | Supported formats : .doc, .docx, .pdf, .jpg, .png, .jpeg, .txt, .xls, .xlsx,
                  .csv
                </span>
                {files.length > 0 && (
                  <div className="note-table mt-10">
                    <div className="table-border">
                      {files.map((file, index) => (
                        <div
                          key={`${file.name}-${file.size}-${index}`}
                          className="clickable d-flex align-items-center justify-space-between"
                        >
                          <div>{file.name}</div>
                          <div className="close mt-5">
                            <a
                              href="#"
                              aria-label={`Remove ${file.name}`}
                              onClick={(event) => {
                                event.preventDefault();
                                setFiles((current) => current.filter((_, i) => i !== index));
                              }}
                            >
                              <Icon name="close" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button className="action-button primary-button" busy={isSending} onClick={submit}>
                <Icon name="send" />
                <span>Submit</span>
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
